import {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {PRODUCT_FILE_MAX_BYTES,rejectProductFile} from '@hisense/shared';
import {Button} from '@/components/ui/Button';
import {ErrorState,LoadingState} from '@/components/ui/States';
import {useProductFiles,useProductFileUpload,useDeleteProductFile} from '@/api/hooks';
import {getLocalizedError} from '@/lib/api';

/**
 * Kho tệp của sản phẩm — CHỈ admin, tệp tới 500MB.
 *
 * Tệp lớn được cắt tại trình duyệt rồi tải lên từng phần: gói Cloudflare của
 * zone chặn thân yêu cầu ở 100MB và Worker chỉ có 128MB bộ nhớ, nên "tải một
 * phát 500MB" là bất khả thi dù viết mã thế nào. Cắt xong vẫn đi qua đúng
 * domain Hisense để máy ở Trung Quốc tải được.
 *
 * Tải về cũng qua máy chủ (`/api/product-files/<id>/download`) chứ không phát
 * đường dẫn kho công khai — để còn kiểm quyền từng lần.
 */
const mb = (bytes: number) => `${(bytes / (1024 * 1024)).toFixed(bytes > 10 * 1024 * 1024 ? 0 : 1)} MB`;

export function ProductFilesSection({productId,isAdmin}:{productId:string;isAdmin:boolean}){
 const {t}=useTranslation();
 const files=useProductFiles(productId,isAdmin);
 const upload=useProductFileUpload(productId);
 const remove=useDeleteProductFile(productId);
 const [error,setError]=useState<string|null>(null);
 const [progress,setProgress]=useState<{name:string;done:number;total:number}|null>(null);
 if(!isAdmin)return null;
 if(files.isLoading)return <LoadingState/>;
 if(files.error)return <ErrorState message={t('productFiles.error')} onRetry={()=>void files.refetch()}/>;

 const pick=async(file:File|undefined)=>{
  if(!file)return;
  setError(null);
  const rejection=rejectProductFile({fileName:file.name,size:file.size});
  if(rejection){setError(t(`productFiles.reject.${rejection}`,{max:mb(PRODUCT_FILE_MAX_BYTES)}));return;}
  try{await upload.mutateAsync({file,onProgress:(done,total)=>setProgress({name:file.name,done,total})});}
  catch(e){setError(getLocalizedError(e,t,'productFiles.error'));}
  finally{setProgress(null);}
 };

 return <section className="space-y-3">
  <div>
   <h2 className="font-semibold">{t('productFiles.title')}</h2>
   <p className="mt-1 text-xs text-ink-soft">{t('productFiles.intro',{max:mb(PRODUCT_FILE_MAX_BYTES)})}</p>
  </div>
  {files.data?.length===0&&<p className="text-sm text-ink-soft">{t('productFiles.empty')}</p>}
  <ul className="space-y-1">{files.data?.map(f=><li key={f.id} className="flex flex-wrap items-center justify-between gap-2 rounded-sm border border-line px-3 py-2 text-sm">
   <a className="min-w-0 flex-1 truncate text-brand underline" href={`/api/product-files/${f.id}/download`}>{f.fileName}</a>
   <span className="font-mono tabular text-2xs text-ink-soft">{mb(f.sizeBytes)}</span>
   <Button size="sm" variant="ghost" disabled={remove.isPending} onClick={()=>void remove.mutateAsync(f.id)}>{t('common.delete')}</Button>
  </li>)}</ul>
  <label className="inline-flex cursor-pointer items-center gap-2 rounded-sm border border-line-strong px-3 py-2 text-sm">
   <input type="file" className="sr-only" disabled={upload.isPending} onChange={e=>void pick(e.target.files?.[0])}/>
   {upload.isPending?t('productFiles.uploading'):t('productFiles.choose')}
  </label>
  {progress&&<p className="text-xs text-ink-soft">{t('productFiles.progress',{name:progress.name,done:String(progress.done),total:String(progress.total)})}</p>}
  {error&&<p role="alert" className="text-sm text-danger">{error}</p>}
 </section>;
}
